import {Component, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from "@angular/forms";

import {MaterialModule} from "../../../../material.module";
import {ToastService} from "../../../../services/layout/toast.service";
import {environment} from "../../../../../environments/environment";
import {ITaskOptions} from "../../../../models/task-options.model";
import {TasksService} from "../../../../services/objects/tasks.service";
import {interval, Subscription, takeWhile} from "rxjs";
import {switchMap} from "rxjs/operators";
import {Marked} from "marked";
import {ContentInterpreterService} from "../../../../services/content-interpreter.service";
import {ActivatedRoute, Router} from "@angular/router";

@Component({
  selector: 'app-task-runner',
  templateUrl: './task-runner.component.html',
  styleUrls: ['./task-runner.component.scss'],
    imports: [MaterialModule, ReactiveFormsModule, FormsModule]
})
export class TaskRunnerComponent implements OnInit, OnDestroy {
    tasksService = inject(TasksService);
    private snackBar = inject(ToastService);
    private markedService = inject(Marked);
    private contentInterpreter = inject(ContentInterpreterService);
    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);

  @Input() task!: ITaskOptions;

  isRunning = false;
  runningTask: any;
  payload = '';
  responseText = '';
  responseAudio: any = '';

  private pollingSubscription!: Subscription;

  ngOnInit(): void {
    // Check query params for a run to resume.
    this.activatedRoute.queryParams.subscribe(params => {
      if (params['runId'] && params['taskId']) {
        console.log('Resuming task run:', params['runId']);
        this.resumeTask(params['taskId'], params['runId']);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  runTest() {
    if (!this.task) return;
    this.snackBar.showInfo('Running task', 3000);
    this.isRunning = true;

    this.tasksService.runTaskAsync(this.task.id, { text: this.payload })
        .subscribe(initialTask => {
          this.runningTask = initialTask;
          // Store both the task and run IDs in the URL.
          this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: { taskId: this.task.id, runId: initialTask.id },
            queryParamsHandling: 'merge'
          });
          this.startPolling(this.task.id, initialTask.id);
        });
  }

  private startPolling(taskId: string, runId: string) {
    this.pollingSubscription = interval(5000).pipe(
        switchMap(() => this.tasksService.getTaskAsync(taskId, runId)),
        takeWhile(task => !task.completedSuccessfully, true)
    ).subscribe(updatedTask => {
      this.runningTask = updatedTask;
      if (updatedTask.completedSuccessfully || updatedTask.attempts >= 5) {
        this.isRunning = false;
        this.processOutputs(updatedTask);
      }
    });
  }

  private resumeTask(taskId: string, runId: string) {
    this.isRunning = true;
    this.startPolling(taskId, runId);
  }

  private processOutputs(updatedTask: any) {
    if (updatedTask.taskExecutionResult &&
        updatedTask.taskExecutionResult.outputs &&
        updatedTask.taskExecutionResult.outputs.length > 0) {

      const textOutput = updatedTask.taskExecutionResult.outputs.find(
          (o: any) => o.label === 'responseText'
      );
      const audioOutput = updatedTask.taskExecutionResult.outputs.find(
          (o: any) => o.label === 'responseAudio'
      );

      if (textOutput && textOutput.base64Data && textOutput.contentType) {
        const interpretedText = this.contentInterpreter.interpretContent(
            textOutput.contentType,
            textOutput.base64Data
        );
        this.responseText = typeof interpretedText === 'string'
            ? this.markedService.parse(interpretedText) as string
            : '';
      } else {
        this.responseText = '';
      }

      if (audioOutput && audioOutput.base64Data && audioOutput.contentType) {
        const interpretedAudio = this.contentInterpreter.interpretContent(
            audioOutput.contentType,
            audioOutput.base64Data
        );
        this.responseAudio = typeof interpretedAudio === 'string' ? interpretedAudio : '';
      } else {
        this.responseAudio = '';
      }
      this.snackBar.showSuccess('Test run successfully', 3000);
    }
  }

  protected readonly environment = environment;
}
